import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api } from '@/lib/api'
import type { TaskType } from '@/types'

export type TaskProgressStage = 'script' | 'images' | 'videos'
export type TaskViewMode = 'foreground' | 'background'

// Configuration
const TASK_CONFIG = {
  MAX_AGE_MS: 60 * 60 * 1000,           // 1 hour hard timeout for pending/processing tasks
  PENDING_PLACEHOLDER_MAX_AGE_MS: 5 * 60 * 1000, // Allow slow task-start requests to resolve
  CLEANUP_INTERVAL_MS: 30 * 1000,       // 30 seconds - cleanup check interval
  POLLING_INTERVAL_MS: 5000,             // 5 seconds - polling interval
  MAX_POLLING_ATTEMPTS: 720,             // 1 hour max polling (720 * 5s)
}

const TASK_TIMEOUT_ERROR = 'Task timeout after 1 hour'

export interface RunningTask {
  id: string
  type: TaskType
  status: 'pending' | 'processing' | 'completed' | 'failed'
  viewMode?: TaskViewMode
  progressStage?: TaskProgressStage
  result?: any  // Task-specific result (video URL, image URLs, etc.)
  error?: string
  createdAt: number
  completedAt?: number  // When task completed/failed
  metadata?: Record<string, any>  // Store task-specific metadata (aspectRatio, productId, etc.)
}

interface TasksState {
  // Running tasks by task ID
  tasks: Record<string, RunningTask>

  // Active polling timers (not persisted, kept in memory only)
  pollingTimers: Record<string, ReturnType<typeof setTimeout>>

  // Actions
  addTask: (task: Omit<RunningTask, 'createdAt'>) => void
  updateTask: (taskId: string, updates: Partial<RunningTask>) => void
  setTaskViewMode: (taskId: string, viewMode: TaskViewMode) => void
  setTasksViewMode: (taskIds: string[], viewMode: TaskViewMode) => void
  removeTask: (taskId: string) => void
  removeCompletedTask: (taskId: string) => void  // Only remove if completed/failed (for page unmount)
  cleanupExpiredTasks: () => void
  autoCleanupProcessingTasks: () => void  // Auto cleanup expired processing tasks (called by timer)
  getTask: (taskId: string) => RunningTask | undefined

  // Polling control
  startPolling: (taskId: string, onComplete?: (task: RunningTask) => void) => void
  stopPolling: (taskId: string) => void
  stopAllPolling: () => void
  isPolling: (taskId: string) => boolean
}

export const useTasksStore = create<TasksState>()(
  persist(
    (set, get) => ({
      tasks: {},
      pollingTimers: {},

      addTask: (task) => {
        const newTask: RunningTask = {
          ...task,
          viewMode: task.viewMode ?? 'foreground',
          createdAt: Date.now(),
        }
        set((state) => ({
          tasks: {
            ...state.tasks,
            [task.id]: newTask,
          },
        }))
      },

      updateTask: (taskId, updates) => {
        set((state) => {
          const currentTask = state.tasks[taskId]
          if (!currentTask) {
            return state
          }

          return {
            tasks: {
              ...state.tasks,
              [taskId]: {
                ...currentTask,
                ...updates,
                // Record completion time
                ...(updates.status === 'completed' || updates.status === 'failed'
                  ? { completedAt: Date.now() }
                  : {}),
              },
            },
          }
        })
      },

      setTaskViewMode: (taskId, viewMode) => {
        set((state) => {
          const currentTask = state.tasks[taskId]
          if (!currentTask) {
            return state
          }

          return {
            tasks: {
              ...state.tasks,
              [taskId]: {
                ...currentTask,
                viewMode,
              },
            },
          }
        })
      },

      setTasksViewMode: (taskIds, viewMode) => {
        if (taskIds.length === 0) {
          return
        }

        const taskIdSet = new Set(taskIds)
        set((state) => {
          let changed = false
          const nextTasks: Record<string, RunningTask> = { ...state.tasks }

          Object.entries(state.tasks).forEach(([taskId, task]) => {
            if (!taskIdSet.has(taskId) || task.viewMode === viewMode) {
              return
            }
            nextTasks[taskId] = {
              ...task,
              viewMode,
            }
            changed = true
          })

          return changed ? { tasks: nextTasks } : state
        })
      },

      removeTask: (taskId) => {
        const { stopPolling } = get()
        stopPolling(taskId)

        set((state) => {
          const { [taskId]: _task, ...remainingTasks } = state.tasks
          return {
            tasks: remainingTasks,
          }
        })
      },

      // Only remove if the task is completed or failed
      // Called when user leaves the page - safe to cleanup finished tasks
      removeCompletedTask: (taskId) => {
        const task = get().tasks[taskId]
        if (task && (task.status === 'completed' || task.status === 'failed')) {
          get().removeTask(taskId)
        }
      },

      cleanupExpiredTasks: () => {
        const now = Date.now()
        set((state) => {
          const activeTasks: Record<string, RunningTask> = {}
          Object.entries(state.tasks).forEach(([id, task]) => {
            const age = now - task.createdAt
            const isExpired = age > TASK_CONFIG.MAX_AGE_MS

            // Clean up pending-* placeholders after the request-start grace window.
            if (id.startsWith('pending-') && age > TASK_CONFIG.PENDING_PLACEHOLDER_MAX_AGE_MS) {
              console.log('[TasksStore] Removing stale pending placeholder:', id)
              return
            }

            // On page refresh, completed/failed tasks are cleaned up.
            // For expired active tasks, mark failed instead of silently removing.
            if (task.status === 'processing' || task.status === 'pending') {
              if (isExpired) {
                activeTasks[id] = {
                  ...task,
                  status: 'failed',
                  progressStage: undefined,
                  error: TASK_TIMEOUT_ERROR,
                  completedAt: now,
                }
                return
              }
              activeTasks[id] = task
            }
          })
          return { tasks: activeTasks }
        })
      },

      // Auto cleanup expired processing tasks (called by timer)
      // Does NOT affect completed/failed tasks - they stay until user leaves page
      autoCleanupProcessingTasks: () => {
        const now = Date.now()
        const { stopPolling } = get()

        set((state) => {
          const activeTasks: Record<string, RunningTask> = {}
          Object.entries(state.tasks).forEach(([id, task]) => {
            const age = now - task.createdAt
            const isExpired = age > TASK_CONFIG.MAX_AGE_MS

            // Mark expired processing/pending tasks as failed
            if ((task.status === 'processing' || task.status === 'pending') && isExpired) {
              console.log('[TasksStore] Marking expired task as failed:', id)
              stopPolling(id)
              activeTasks[id] = {
                ...task,
                status: 'failed',
                progressStage: undefined,
                error: TASK_TIMEOUT_ERROR,
                completedAt: now,
              }
              return
            }

            // Keep all other tasks (including completed/failed)
            activeTasks[id] = task
          })
          return { tasks: activeTasks }
        })
      },

      getTask: (taskId) => {
        return get().tasks[taskId]
      },

      startPolling: (taskId, onComplete) => {
        // Don't poll for pending-* placeholder tasks - they are local-only and don't exist on the backend
        if (taskId.startsWith('pending-')) {
          console.log('[TasksStore] Skipping polling for pending placeholder:', taskId)
          return
        }

        const { updateTask, stopPolling, pollingTimers } = get()

        // Clear existing timer if any
        if (pollingTimers[taskId]) {
          clearTimeout(pollingTimers[taskId])
        }

        let attempts = 0
        const startTime = Date.now()

        // Simulate progress stages based on elapsed time
        const getSimulatedProgressStage = (elapsedSeconds: number): TaskProgressStage | undefined => {
          if (elapsedSeconds < 30) {
            return 'script'  // 0-30s: Generating script
          } else if (elapsedSeconds < 60) {
            return 'images'  // 30-60s: Generating images
          } else {
            return 'videos'  // 60s+: Generating videos
          }
        }

        const poll = async () => {
          try {
            const task = await api.getTask(taskId)
            const elapsedSeconds = (Date.now() - startTime) / 1000

            if (task.status === 'completed') {
              // Get task results
              const results = await api.getTaskResults(taskId)
              const currentTask = get().tasks[taskId]

              const completedTask: RunningTask = {
                ...currentTask,
                id: taskId, // Ensure id is always set
                status: 'completed',
                progressStage: undefined,
                result: results,
              }

              updateTask(taskId, completedTask)
              stopPolling(taskId)

              if (onComplete) {
                onComplete(completedTask)
              }
              return
            } else if (task.status === 'failed') {
              updateTask(taskId, {
                status: 'failed',
                progressStage: undefined,
                error: task.error_message || 'Task failed',
              })
              stopPolling(taskId)
              return
            }

            // Still processing - use simulated progress based on elapsed time
            const simulatedStage = getSimulatedProgressStage(elapsedSeconds)

            updateTask(taskId, {
              status: 'processing',
              progressStage: simulatedStage,
            })

            attempts++
            if (attempts < TASK_CONFIG.MAX_POLLING_ATTEMPTS) {
              const timer = setTimeout(poll, TASK_CONFIG.POLLING_INTERVAL_MS)
              set((state) => ({
                pollingTimers: {
                  ...state.pollingTimers,
                  [taskId]: timer,
                },
              }))
            } else {
              updateTask(taskId, {
                status: 'failed',
                progressStage: undefined,
                error: `${TASK_TIMEOUT_ERROR}, please check Tasks page`,
              })
              stopPolling(taskId)
            }
          } catch (error) {
            updateTask(taskId, {
              status: 'failed',
              progressStage: undefined,
              error: 'Failed to check task status',
            })
            stopPolling(taskId)
          }
        }

        // Start polling
        poll()
      },

      stopPolling: (taskId) => {
        const { pollingTimers } = get()
        const timer = pollingTimers[taskId]
        if (timer) {
          clearTimeout(timer)
          set((state) => {
            const { [taskId]: _, ...remainingTimers } = state.pollingTimers
            return { pollingTimers: remainingTimers }
          })
        }
      },

      stopAllPolling: () => {
        const { pollingTimers } = get()
        Object.values(pollingTimers).forEach((timer) => {
          clearTimeout(timer)
        })
        set({ pollingTimers: {} })
      },

      isPolling: (taskId) => {
        return !!get().pollingTimers[taskId]
      },
    }),
    {
      name: 'tasks-storage',
      // Only persist task data, not timers
      partialize: (state) => ({
        tasks: state.tasks,
      }),
      // On rehydrate: cleanup expired and completed tasks, restart polling for active ones
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Cleanup expired tasks and all completed/failed tasks
          // (user refreshed page, they're no longer viewing results)
          state.cleanupExpiredTasks()

          // Only restart polling if user has a valid token
          // This prevents UNAUTHORIZED errors on login page refresh
          const hasToken = !!localStorage.getItem('access_token')
          if (!hasToken) {
            return
          }

          // Restart polling for any still-processing tasks
          // Note: Don't poll for 'pending-*' tasks - they are local placeholders waiting for API response
          Object.entries(state.tasks).forEach(([taskId, task]) => {
            if (task.status === 'processing' && !taskId.startsWith('pending-')) {
              state.startPolling(taskId)
            }
          })
        }
      },
    }
  )
)

// Global cleanup timer for expired processing tasks
let cleanupInterval: ReturnType<typeof setInterval> | null = null

export const startAutoCleanup = () => {
  if (cleanupInterval) return
  cleanupInterval = setInterval(() => {
    useTasksStore.getState().autoCleanupProcessingTasks()
  }, TASK_CONFIG.CLEANUP_INTERVAL_MS)
}

export const stopAutoCleanup = () => {
  if (cleanupInterval) {
    clearInterval(cleanupInterval)
    cleanupInterval = null
  }
}

// Auto-start cleanup timer when module loads (browser only)
if (typeof window !== 'undefined') {
  startAutoCleanup()
}
